'use client';
import { useId, useRef } from 'react';
import type { FieldError, UseFormRegisterReturn } from 'react-hook-form';

import type { TModalProps } from '~components/Modals/types';
import { Popover } from '~components/Popover';
import { P } from '~components/Typography';
import { useModal } from '~hooks/useModal';
import { Textarea } from '~shared/ui/atoms';

import {
	Description,
	Error,
	InfoButton,
	Label,
	LabelRow,
	TooltipBox,
	TooltipPointer,
	Wrapper,
} from './styles';

type Props = {
	registration: UseFormRegisterReturn;
	label?: React.ReactNode;
	placeholder?: string;
	tooltip?: React.ReactNode;
	rows?: number;
	maxLength?: number;
	error?: FieldError['message'];
	description?: string;
	renderInfo?: (props: TModalProps) => JSX.Element;
	disabled?: boolean;
	className?: string;
	id?: string;
	onTooltipShow?: () => void;
};

export const TextareaField = ({
	registration,
	label,
	placeholder,
	tooltip,
	rows,
	maxLength,
	error,
	description,
	renderInfo,
	disabled = false,
	className,
	id: idFromProps,
	onTooltipShow,
	...props
}: Props) => {
	const id = useId();

	const tooltipRef = useRef<HTMLDivElement>(null);

	const { open, isOpen, close } = useModal();

	return (
		<>
			<Wrapper>
				{label && (
					<LabelRow>
						<Label htmlFor={idFromProps || id}>{label}</Label>
						{renderInfo ? (
							<InfoButton type="button" onClick={open()}>
								?
							</InfoButton>
						) : tooltip ? (
							<TooltipPointer ref={tooltipRef}>
								<P size="small" weight={500} transparent={true}>
									?
								</P>
							</TooltipPointer>
						) : null}
					</LabelRow>
				)}
				{tooltip && !renderInfo && (
					<Popover ref={tooltipRef} onShow={onTooltipShow}>
						<TooltipBox>
							<P size="small" align="left">
								{tooltip}
							</P>
						</TooltipBox>
					</Popover>
				)}
				<Textarea
					id={idFromProps || id}
					className={className}
					aria-invalid={!!error}
					registration={registration}
					placeholder={placeholder}
					isError={!!error}
					rows={rows}
					maxLength={maxLength}
					disabled={disabled}
					{...props}
				/>
				{error ? (
					<Error>
						<P size="small" align="left" danger={true}>
							{error}
						</P>
					</Error>
				) : description ? (
					<Description>
						<P size="small" align="left" transparent={true}>
							{description}
						</P>
					</Description>
				) : null}
			</Wrapper>

			{renderInfo &&
				renderInfo({
					open: isOpen,
					locked: false,
					onClose: close,
				})}
		</>
	);
};
